/**
 * @fileoverview OAuth Callback Handler Component
 * @description Handles the redirect back from the backend after Ping authentication.
 *              The backend redirects here with a one-time code that the frontend
 *              exchanges for actual tokens via POST /api/auth/exchange.
 * @module pages/Callback
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { useAuth } from '@/context';
import './Callback.css';

export const Callback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleCodeExchange } = useAuth();
  const [error, setError] = useState(null);
  const [exchanged, setExchanged] = useState(false);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(errorParam);
      return;
    }

    const code = searchParams.get('code');
    if (!code) {
      setError('Missing authorization code.');
      return;
    }

    if (exchanged) return;

    // Exchange the one-time code for tokens
    handleCodeExchange(code).then((success) => {
      setExchanged(true);
      if (success) {
        const returnUrl = searchParams.get('return_url') || sessionStorage.getItem('returnUrl') || '/';
        sessionStorage.removeItem('returnUrl');
        navigate(returnUrl, { replace: true });
      } else {
        setError('Failed to complete sign-in. Please try again.');
      }
    });
  }, [searchParams, handleCodeExchange, navigate, exchanged]);

  if (error) {
    return (
      <Box className="callback-page callback-page--error">
        <Alert severity="error" className="callback-page__alert">
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Authentication Failed
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Typography
          variant="body2"
          color="primary"
          className="callback-page__link"
          onClick={() => navigate('/login')}
        >
          Return to login
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="callback-page">
      <Spinner size={48} />
      <Typography variant="h6" color="text.secondary">
        Completing sign in...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we verify your credentials
      </Typography>
    </Box>
  );
};

export default Callback;
